/**
 * useFileDropZone Hook
 *
 * Headless, UI-free drag-and-drop file zone. Owns the drag-counter state machine
 * that keeps an overlay stable while the pointer moves over nested children (the
 * classic dragenter/dragleave flicker), and surfaces dropped files via `onDrop`.
 *
 * The consumer renders its own overlay using `isDragActive` and spreads
 * `dropZoneProps` onto the target element — keeping presentation at the call site
 * (the convention used by DocumentUploader and the chat composer). It only reacts
 * to native file drags (`dataTransfer.types` includes `'Files'`), so dragging text
 * or page elements never activates it.
 *
 * Example:
 *   const { isDragActive, dropZoneProps } = useFileDropZone({ onDrop: handleFiles });
 *   return (
 *     <div className="relative" {...dropZoneProps}>
 *       {children}
 *       {isDragActive && <DropOverlay />}
 *     </div>
 *   );
 */

import { useCallback, useRef, useState, type DragEvent } from 'react';

export interface UseFileDropZoneOptions {
  /** Called with the dropped File objects (empty drops are not forwarded). */
  onDrop: (files: File[]) => void;
  /** When true, the zone never activates and drops are ignored. */
  disabled?: boolean;
}

export interface FileDropZoneProps {
  onDragEnter: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
}

export interface UseFileDropZoneResult {
  /** True while a file drag is hovering the zone — drive the overlay with this. */
  isDragActive: boolean;
  /** Drag handlers to spread onto the drop target element. */
  dropZoneProps: FileDropZoneProps;
}

/** A drag carries files (vs. text/elements) when its types list includes "Files". */
function dragHasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

export function useFileDropZone({
  onDrop,
  disabled = false,
}: UseFileDropZoneOptions): UseFileDropZoneResult {
  const [isDragActive, setIsDragActive] = useState(false);
  // Counts enter/leave across nested children so the overlay doesn't flicker when
  // the pointer crosses a child boundary (dragleave on a child fires before
  // dragenter on its sibling).
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      if (disabled || !dragHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current += 1;
      if (dragCounter.current === 1) setIsDragActive(true);
    },
    [disabled],
  );

  const handleDragOver = useCallback(
    (e: DragEvent) => {
      if (disabled || !dragHasFiles(e)) return;
      // preventDefault is required for the element to be a valid drop target and
      // to suppress the browser's default "open file" behavior.
      e.preventDefault();
      e.stopPropagation();
    },
    [disabled],
  );

  const handleDragLeave = useCallback(
    (e: DragEvent) => {
      if (disabled || !dragHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current -= 1;
      if (dragCounter.current <= 0) {
        dragCounter.current = 0;
        setIsDragActive(false);
      }
    },
    [disabled],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragActive(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) onDrop(files);
    },
    [disabled, onDrop],
  );

  return {
    isDragActive,
    dropZoneProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}

export default useFileDropZone;
